/* eslint-disable no-underscore-dangle */

'use strict';

define('vb/private/stateManagement/router/urlParamAdapter',[
  'vb/private/stateManagement/router/urlAdapter',
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/stateManagement/router/stateChange',
  'vb/private/log',
], (UrlAdapter, VbRouter, StateChange, Log) => {
  const log = Log.getLogger('/vb/stateManagement/router/urlParamAdapter');

  /**
   * Return key/value object of query parameters.
   * @return {!Object}
   */
  const parseQueryParam = (queryString) => {
    const params = {};

    // Remove starting '?'
    const trimmedQueryString = queryString.split('?')[1];

    if (trimmedQueryString) {
      const keyValPairs = trimmedQueryString.split('&');
      keyValPairs.forEach((pair) => {
        const parts = pair.split(/=(.+)?/);
        const key = parts[0];

        if (key.length) {
          const value = parts[1] && decodeURIComponent(parts[1]);
          params[key] = value;
        }
      });
    }

    return params;
  };

  /**
   *  Url adapter used by the {@link VbRouter} to manage URL in the form of
   * <code class="prettyprint">/index.html?root=book&book=chapter2</code>.  This adapter
   * can be used if the {@link VbRouter.urlPathAdapter|urlPathAdapter} doesn't meet
   * the application's needs.
   * <br>This adapter is well-suited for single-page applications whose entry point
   * is always a single document, i.e., "index.html" which restores its router state
   * from additional parameters.  The router state is encoded as URL parameters and
   * then restored after the page is loaded.  This is ideal for applications which
   * cannot handle multiple entry points, as recommended by {@link VbRouter.urlPathAdapter|urlPathAdapter}.
   * <br>There are two available
   * URL adapters, this one and the {@link VbRouter.urlPathAdapter|urlPathAdapter}.<br>To change
   * the URL adapter, use the {@link VbRouter.defaults|urlAdapter} property.
   * @see UrlPathAdapter
   * @see VbRouter.defaults
   * @example <caption>Change the default URL adapter to urlParamAdapter instead of urlPathAdapter:</caption>
   * VbRouter.defaults['urlAdapter'] = new UrlParamAdapter();
   */
  class UrlParamAdapter extends UrlAdapter {
    /**
     * Construct an array of states where each item is an object made of a router and
     * the new state for it.
     * @param {Object} transition An object with properties describing the transition.
     * @return {!Array.<StateChange>}
     * @throws Error when parsing of router param fails.
     */
    // eslint-disable-next-line class-methods-use-this
    parse(transition) {
      const { search } = window.location;
      const params = parseQueryParam(search);
      let router = transition.rootRouter;
      let changes = [];

      log.finer('Parsing:', search);

      while (router) {
        let value = params[router._name] || router._defaultStateId;

        // Retrieve all value separated by '/'
        const segments = VbRouter.getSegments(value);
        value = segments.shift();

        const stateChange = new StateChange(router, value);

        if (value) {
          const state = stateChange.getState();

          // If this state has parameters, retrieve their values from the segments
          if (state) {
            state._paramOrder.forEach(() => {
              stateChange.addParameter(segments.shift());
            });
          }

          changes.push(stateChange);
        }
        router = router._getChildRouter(value);
      }

      changes = VbRouter.appendOtherChanges(changes, transition.rootRouter);

      return changes;
    }

    /**
     * Given an ordered array of states, build the URL representing all
     * the states.
     * Never starts with a '/': "index.html", "book/chapter2"
     * @param {!Array.<StateChange>} newStates
     * @return {!string} the URL representing the states
     * @throws An error if bookmarkable state is too big.
     */
    // eslint-disable-next-line class-methods-use-this
    buildUrlFromStates(newStates) {
      let canDefault = false;
      let search = '';

      // Build the new URL by walking the array of states backward in order to eliminate
      // the default state from the URL. As soon as a value is not the default, stops the removal.
      for (let ns = newStates.pop(); ns; ns = newStates.pop()) {
        if (ns.value) {
          if (canDefault || (ns.value !== ns.router._defaultStateId)) {
            // _name is already encoded
            const paramName = `&${ns.router._name}=`;
            const paramValue = ns.value;

            // Because we are traversing the array backward, insert instead of append
            search = paramName + encodeURIComponent(paramValue) + search;
            canDefault = true;
          }
        }
      }

      // Replace first parameter separator from '&' to '?'
      if (search) {
        search = `?${search.substr(1)}`;
      }

      return VbRouter.buildUrl({ search });
    }
  }

  return UrlParamAdapter;
});

